import {afterNextRender, Component, computed, effect, inject, input, PLATFORM_ID, ResourceStatus} from '@angular/core';
import {SubscriptionContextV2Service} from '../../../../services/subscription-v2-context.service';
import {LeadResources} from '../../../../resources/lead.resources';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FIREBASE_AUTH} from '../../../config/module.di.tokens';
import {InputComponent} from '../../common/form/input/input.component';
import {CheckboxComponent} from '../../common/form/checkbox/checkbox.component';
import {PhoneComponent} from '../../common/form/phone/phone.component';
import {SubDiscountCodeComponent} from '../sub-discount-code/sub-discount-code.component';
import {ShowPricePipe} from '../../../../common/pipe/showPrice.pipe';
import {SubscriptionResources} from '../../../../resources/subscription.resources';
import {isPlatformBrowser, NgIf} from '@angular/common';
import {createE164PhoneNumber, e164ToInputPhoneNumberFormat} from '../../../../common/strings';
import {Lead} from '../../../../data/domain/lead';
import {jwtToken} from '../../../../common/jwt';
import {Router, RouterLink} from '@angular/router';
import {SocialTrackService} from '../../../../services/social-track.service';
import {signOut} from '@firebase/auth';
import {addDefaultAnimationIfNeeded} from '../../../helper/lead.helper';
import {SessionResources} from '../../../../resources/session.resources';
import {SubNavbarV2Component} from '../sub-navbar-v2/sub-navbar-v2.component';
import {Sex} from '@minutespourtoi/api-models/dist/models';
import {PaypalPreloaderService} from '../../../../services/paypal-preload.service';

@Component({
  selector: 'app-sub-v2-finalize',
  imports: [
    ReactiveFormsModule,
    InputComponent,
    CheckboxComponent,
    PhoneComponent,
    SubDiscountCodeComponent,
    ShowPricePipe,
    NgIf,
    RouterLink,
    SubNavbarV2Component
  ],
  templateUrl: './sub-v2-finalize.component.html'
})
export class SubV2FinalizeComponent {

  sessionId = input<string>()
  referralCode = input<string>()
  sessionResources = inject(SessionResources)
  subscriptionContextV2Service = inject(SubscriptionContextV2Service)
  subscriptionResources = inject(SubscriptionResources)
  socialTrackService = inject(SocialTrackService)
  leadResources = inject(LeadResources)
  formBuilder = inject(FormBuilder)
  auth = inject(FIREBASE_AUTH)
  router = inject(Router)
  platformId = inject(PLATFORM_ID)
  paypalPreloader = inject(PaypalPreloaderService)

  finalizeForm = new FormGroup({
    phone: new FormControl(null, Validators.compose([Validators.required, Validators.minLength(10)])),
    firstname: new FormControl(null, Validators.required),
    reducedMobility: new FormControl(null, null),
    cgv: new FormControl(null, Validators.requiredTrue),
  })
  phoneAlreadyUsed = computed(() => (this.subscriptionResources.calculatePrice.error() as any)?.status === 412)
  showBadPhone = false
  showBadFirstname = false

  constructor() {
    effect(() => this.currentLeadLoaded());
    if (isPlatformBrowser(this.platformId)) {
      afterNextRender(() => {
        requestIdleCallback(() => this.paypalPreloader.preload(), { timeout: 3000 });
      });
    }
  }

  async currentLeadLoaded() {
    const currentLeadStatus = this.leadResources.currentLead.status()
    const session = this.sessionResources.session.value()

    if(currentLeadStatus == ResourceStatus.Resolved) {
      const currentLead = this.leadResources.currentLead.value()
      if(currentLead) {
        if (!currentLead.offer) {
          currentLead.offer = await this.subscriptionContextV2Service.detectDefaultOffer()
        }
        if(session) {
          await addDefaultAnimationIfNeeded(currentLead, session)
        }
        if (currentLead?.isValidForConfirmation()) {
          await this.updateFormFromLeadOrAuthUser(currentLead)
        }
        this.updatePrice()
      }
    }
  }

  changeFirstname() {
    this.updatePrice()
    this.socialTrackService.trackGAEvent('finalize_input_firstname')
  }

  isEligibleForDiscount() {
    const offerCode = this.leadResources.currentLead.value()?.offer?.code
    return  offerCode != 'm-essentiel' && offerCode != 'w-essentiel'
  }

  updatePrice() {
    const currentLead = this.leadResources.currentLead.value();
    if (currentLead && this.finalizeForm.controls.phone.value && this.finalizeForm.controls.firstname.value) {

      if(this.referralCode()) {
        if(!currentLead.discount?.discountCode && this.isEligibleForDiscount()) {
          currentLead.discount = {
            discountCode: this.referralCode()
          }
        } else if (!this.isEligibleForDiscount()) {
          currentLead.discount = null
        }
      }

      this.subscriptionResources.calculatePriceRequest.set({
        options: currentLead?.options.map(opt => opt.code),
        sex: currentLead.sex as Sex,
        discountCode: currentLead.discount?.discountCode,
        sessionId: currentLead.sessionId,
        subscriptionId : currentLead.subscription?.id,
        firstname: this.finalizeForm.controls.firstname.value,
        offerCode: currentLead.offer.code,
        phoneNumber: createE164PhoneNumber(this.finalizeForm.controls.phone.value),
      })
    }
    this.socialTrackService.trackGAEvent('finalize_input_phone')
  }

  private async updateFormFromLeadOrAuthUser(leadDTO: Lead) {
    let phone: string
    let firstname: string
    if (leadDTO) {
      phone = leadDTO.phone
      firstname = leadDTO.firstname
    }
    if (this.auth.currentUser) {
      const token = await jwtToken(this.auth.currentUser)
      if (token?.firstname && !firstname) {
        firstname = token.firstname
      }
      if (this.auth.currentUser.phoneNumber && !phone) {
        phone = this.auth.currentUser.phoneNumber
      }
    }
    this.finalizeForm.controls.phone.setValue(e164ToInputPhoneNumberFormat(phone));
    this.finalizeForm.controls.firstname.setValue(firstname);
    this.finalizeForm.controls.reducedMobility.setValue(leadDTO?.reducedMobility ?? false);
  }

  async next() {
    if (this.finalizeForm.valid) {

      const phone = createE164PhoneNumber(this.finalizeForm.controls.phone.value)
      const currentLead = this.leadResources.currentLead.value();

      if(currentLead) {
        if (currentLead.phone && currentLead.phone != phone) {
          await signOut(this.auth)
          currentLead.subscription = null
          currentLead.discount = null
        }
        currentLead.phone = phone
        currentLead.firstname = this.finalizeForm.controls.firstname.value;
        currentLead.reducedMobility = this.finalizeForm.controls.reducedMobility.value;

        if (!this.subscriptionResources.calculatePrice.hasValue()) {
          this.updatePrice()
        }
        this.leadResources.saveLeadRequest.set(currentLead)
        this.socialTrackService.trackGAEvent('finalize_next')

        await this.router.navigate(['/subscribe-v2', this.sessionId(), 'pay'], {
          queryParams: {
            referralCode: this.referralCode()
          }
        })
      }
    } else {
      if(this.finalizeForm.controls.phone.invalid) {
        this.finalizeForm.controls.phone.markAsTouched();
        this.showBadPhone = true
      }
      if(this.finalizeForm.controls.firstname.invalid) {
        this.finalizeForm.controls.firstname.markAsTouched();
        this.showBadFirstname = true
      }

    }
  }

}
